import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Stack,
  Divider,
  useTheme,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as TrophyIcon,
  ShowChart as ShowChartIcon
} from '@mui/icons-material';
import { financeService } from '../services/financeService';

const RafflePerformanceAnalytics = ({ filters = {} }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    loadPerformanceData(1); // Sempre carregar da página 1 inicialmente
  }, [pageSize]); // Recarregar quando o tamanho da página mudar

  const loadPerformanceData = async (page = currentPage) => {
    try {
      setLoading(true);
      setError(null);

      // Para o ranking de performance, sempre buscamos TODO o período 
      // disponível no banco de dados, ignorando filtros de data
      const params = {
        page: page,
        page_size: pageSize
      };
      
      // Removemos qualquer filtro de data para garantir que pegue 100% dos dados
      // if (filters.startDate) {
      //   params.start_date = filters.startDate.toISOString();
      // }
      // if (filters.endDate) {
      //   params.end_date = filters.endDate.toISOString();
      // }

      console.log('Fazendo requisição para ranking de performance com params:', params);
      const data = await financeService.getRafflePerformanceRanking(params);
      console.log('Dados recebidos:', data);
      setPerformanceData(data);
      setCurrentPage(page);
    } catch (err) {
      console.error('Erro ao carregar dados de performance:', err);
      setError('Erro ao carregar dados de performance dos sorteios');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatNumber = (value, decimals = 2) => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const formatPercentage = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value / 100);
  };

  const formatDifference = (difference) => {
    const isPositive = difference > 0;
    return (
      <Chip
        icon={isPositive ? <TrendingUpIcon /> : <TrendingDownIcon />}
        label={`${isPositive ? '+' : ''}${formatNumber(difference)}`}
        color={isPositive ? 'success' : 'error'}
        variant="outlined"
        size="small"
      />
    );
  };

  const getRankIcon = (index) => {
    const globalPosition = (currentPage - 1) * pageSize + index;
    if (globalPosition === 0) return <TrophyIcon sx={{ color: '#FFD700' }} />;
    if (globalPosition === 1) return <TrophyIcon sx={{ color: '#C0C0C0' }} />;
    if (globalPosition === 2) return <TrophyIcon sx={{ color: '#CD7F32' }} />;
    return <Avatar sx={{ width: 24, height: 24, fontSize: '0.8rem', bgcolor: theme.palette.grey[400] }}>{globalPosition + 1}</Avatar>;
  };

  const handlePageChange = (event, newPage) => {
    loadPerformanceData(newPage + 1); // MUI usa páginas baseadas em 0, nosso backend usa 1
  };

  const handlePageSizeChange = (event) => {
    const newSize = event.target.value;
    setPageSize(newSize);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!performanceData || !performanceData.general_indicator) {
    console.log('Verificação de dados - performanceData:', performanceData);
    console.log('General indicator:', performanceData?.general_indicator);
    return (
      <Alert severity="info">
        Nenhum dado de performance encontrado para o período selecionado.
      </Alert>
    );
  }

  const { general_indicator, raffle_rankings = [] } = performanceData;

  return (
    <Box>
      {/* Card do Indicador Geral */}
      <Card sx={{ mb: 3, background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.primary.main}10)` }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <AssessmentIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight="bold">
              Indicador Geral de Performance
            </Typography>
          </Box>
          
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
            <Box flex={1}>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {formatNumber(general_indicator?.general_performance_score || 0, 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Score Geral de Performance
              </Typography>
            </Box>
            
            <Divider orientation={{ xs: 'horizontal', md: 'vertical' }} flexItem />
            
            <Box flex={2}>
              <Stack direction="row" spacing={4} flexWrap="wrap">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Transações/Hora
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {formatNumber(general_indicator?.general_avg_transactions_hour || 0, 1)}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Ticket Médio
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {formatCurrency(general_indicator?.general_avg_ticket || 0)}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Margem de Lucro
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {formatPercentage(general_indicator?.general_profit_margin || 0)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>
          
          <Typography variant="caption" color="text.secondary" mt={2} display="block">
            Período: {performanceData?.analysis_period || 'Não definido'}
          </Typography>
        </CardContent>
      </Card>

      {/* Tabela de Ranking */}
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <ShowChartIcon sx={{ mr: 1, color: theme.palette.secondary.main }} />
            <Typography variant="h6" fontWeight="bold">
              Ranking de Performance dos Sorteios
            </Typography>
            <Chip 
              label={`${performanceData?.total_raffles || 0} sorteios`} 
              size="small" 
              sx={{ ml: 2 }}
            />
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Posição</TableCell>
                  <TableCell>Sorteio</TableCell>
                  <TableCell align="center">Score</TableCell>
                  <TableCell align="center">Transações/Hora</TableCell>
                  <TableCell align="center">Ticket Médio</TableCell>
                  <TableCell align="center">Margem (%)</TableCell>
                  <TableCell align="center">Diferença vs. Indicador</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {performanceData?.raffle_rankings?.map((raffle, index) => (
                  <TableRow 
                    key={raffle.product_id}
                    sx={{ 
                      backgroundColor: index < 3 ? `${theme.palette.success.main}08` : 'transparent',
                      '&:hover': { backgroundColor: theme.palette.action.hover }
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getRankIcon(index)}
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {raffle.product_title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {raffle.product_id}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {formatNumber(raffle.performance_score, 0)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Typography variant="body2">
                        {formatNumber(raffle.avg_transactions_hour, 1)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Typography variant="body2">
                        {formatCurrency(raffle.avg_ticket)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Typography variant="body2">
                        {formatPercentage(raffle.profit_margin)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="center">
                      {formatDifference(raffle.difference_vs_general)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {(!performanceData?.raffle_rankings || performanceData.raffle_rankings.length === 0) && (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography color="text.secondary">
                Nenhum sorteio encontrado para o período selecionado
              </Typography>
            </Box>
          )}

          {/* Controles de Paginação */}
          {performanceData?.raffle_rankings && performanceData.raffle_rankings.length > 0 && (
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} px={2} pb={1}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Por página</InputLabel>
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  label="Por página"
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>

              <TablePagination
                component="div"
                count={performanceData?.total_raffles || 0}
                page={currentPage - 1} // MUI usa páginas baseadas em 0
                onPageChange={handlePageChange}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[]} // Removemos as opções padrão já que temos nosso próprio seletor
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                }
                labelRowsPerPage=""
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RafflePerformanceAnalytics;